// TODO: delete this file once the Transfer-In Account, Spousal Variable Benefit and account status backends exist.

import { AccountStatusEnum } from '../models/transactions/enum/account-status.enum';
import { AccountStatusType } from '../models/transactions/account-status-type';
import { AccountTypeIdEnum } from '../models/transactions/enum/account-type-id.enum';
import { RelationshipStatus } from '../models/enum/shared/relationship-status';
import { MemberRecord } from '../models/users/dto/member-record';
import { MemberAccount } from '../models/transactions/member-account';
import { MemberUserSearchResult } from '../models/users/dto/member-user-search-result';
import { environment } from '../../environments/environment';

const mockTransferInAccounts: ReturnType<typeof buildMockTransferInAccount>[] = [];
let nextMockTransferInAccountId = 90000001;

const mockSpousalRolloverAccounts = new Map<number, NonNullable<MemberAccount['spousalRolloverAccount']>>();
let nextMockSpousalAccountId = 91000001;

type MockStatusChange = { statusId: AccountStatusEnum; statusChangedDate: string };
const mockStatusChanges = new Map<number, MockStatusChange>();
const mockContributionStatusChangesByMember = new Map<number, MockStatusChange>();

// Bot members on dev that stand in for a rolled-over pair until the account reads return the links (SPP-3390).
// The ids only mean this pair on the dev API, so other environments never get the links.
const isDevApi = environment.mainServiceUrl === 'https://dev.api.gvpensionplan.com';
const mockRolloverSource = { memberId: 121, memberName: 'Eladio Homenick (Bot)', accountId: 328 };
const mockRolloverSpousal = { memberId: 106, memberName: 'Daryl Rutherford (Bot)', accountId: 295 };

function buildMockTransferInAccount(memberId: number) {
  return {
    id: nextMockTransferInAccountId++,
    memberId,
    typeId: AccountTypeIdEnum.TransferInAccount,
    type: { id: AccountTypeIdEnum.TransferInAccount, name: 'Transfer-In Account', idPrefix: 'TI', categoryId: 3 },
    statusId: AccountStatusEnum.Pending,
    status: { id: AccountStatusEnum.Pending, name: 'Pending' },
    employerId: null,
    spouseId: null,
    totalBalance: 0,
    createdDate: new Date().toISOString()
  };
}

export function mockCreateTransferInAccount(memberId: number) {
  const account = buildMockTransferInAccount(memberId);
  mockTransferInAccounts.push(account);
  return account;
}

export function mockGetTransferInAccounts(memberId: number) {
  return mockTransferInAccounts.filter(account => account.memberId === memberId);
}

export function mockGetSpousalVariableBenefitSourceAccount(member: MemberRecord, vbAccount: MemberAccount | undefined) {
  const relationshipStatus = member.activeRelationshipStatus;
  const hasActiveSpouse =
    !!relationshipStatus?.spouse &&
    [RelationshipStatus.Married, RelationshipStatus.CommonLaw, RelationshipStatus.Separated].includes(
      relationshipStatus.typeId
    );
  if (!relationshipStatus || !hasActiveSpouse) {
    return { memberId: member.id, activeSpouse: null, account: null };
  }
  const spouse = relationshipStatus.spouse;
  const spouseName = `${spouse.firstName} ${spouse.lastName}`;
  const canRollOver =
    (vbAccount?.statusId === AccountStatusEnum.Active || vbAccount?.statusId === AccountStatusEnum.Suspended) &&
    !mockSpousalRolloverAccounts.has(vbAccount.id) &&
    !(isDevApi && vbAccount.id === mockRolloverSource.accountId);
  return {
    memberId: member.id,
    activeSpouse: { spouseId: spouse.id, name: spouseName, linkedMemberId: null },
    account:
      vbAccount && canRollOver
        ? {
            accountId: vbAccount.id,
            idPrefix: 'VB',
            createdDate: vbAccount.createdDate,
            specifiedBeneficiary: { name: spouseName, memberId: null },
            minimumAge: { name: spouseName, dateOfBirth: spouse.birthDate, isSpouse: true },
            effectiveYear: new Date().getFullYear(),
            minimumAnnualWithdrawalAmount: 3500,
            struckOnBalance: 618750,
            struckOnDate: `${new Date().getFullYear() - 1}-12-31`,
            yearToDatePaymentAmount: 0
          }
        : null
  };
}

export function mockCreateSpousalVariableBenefitAccount(sourceAccountId: number, successor: MemberUserSearchResult) {
  mockSpousalRolloverAccounts.set(sourceAccountId, {
    spousalAccountId: nextMockSpousalAccountId++,
    spousalAccountIdPrefix: 'VB',
    successorMemberId: successor.id,
    successorName: successor.name,
    spousalAccountStatusId: AccountStatusEnum.Pending
  });
}

export function mockAttachSpousalRolloverLinks(account: MemberAccount): MemberAccount {
  const mockRolloverAccount =
    isDevApi && account.id === mockRolloverSource.accountId
      ? {
          spousalAccountId: mockRolloverSpousal.accountId,
          spousalAccountIdPrefix: 'VB',
          successorMemberId: mockRolloverSpousal.memberId,
          successorName: mockRolloverSpousal.memberName,
          spousalAccountStatusId: AccountStatusEnum.Pending
        }
      : null;
  const spousalRolloverAccount = mockSpousalRolloverAccounts.get(account.id) ?? mockRolloverAccount;
  const spousalRolloverSource =
    isDevApi && account.id === mockRolloverSpousal.accountId
      ? {
          sourceAccountId: mockRolloverSource.accountId,
          sourceAccountIdPrefix: 'VB',
          sourceMemberId: mockRolloverSource.memberId,
          sourceMemberName: mockRolloverSource.memberName,
          sourceAccountStatusId: AccountStatusEnum.Active
        }
      : null;
  return spousalRolloverAccount || spousalRolloverSource
    ? Object.assign(new MemberAccount(), account, {
        spousalRolloverAccount: spousalRolloverAccount ?? account.spousalRolloverAccount,
        spousalRolloverSource: spousalRolloverSource ?? account.spousalRolloverSource
      })
    : account;
}

export function mockChangeAccountStatus(account: MemberAccount, statusId: AccountStatusEnum) {
  const statusChange = { statusId, statusChangedDate: new Date().toISOString() };
  if (account.typeId === AccountTypeIdEnum.ContributionAccount && statusId !== AccountStatusEnum.Struck) {
    mockContributionStatusChangesByMember.set(account.memberId, statusChange);
  } else {
    mockStatusChanges.set(account.id, statusChange);
  }
}

export function mockAttachStatusChange(account: MemberAccount): MemberAccount {
  const isSuspendableContributionAccount =
    account.typeId === AccountTypeIdEnum.ContributionAccount &&
    [AccountStatusEnum.Active, AccountStatusEnum.Suspended].includes(account.statusId);
  const statusChange =
    mockStatusChanges.get(account.id) ??
    (isSuspendableContributionAccount ? mockContributionStatusChangesByMember.get(account.memberId) : undefined);
  return statusChange
    ? Object.assign(new MemberAccount(), account, {
        statusId: statusChange.statusId,
        status: Object.assign(new AccountStatusType(), {
          id: statusChange.statusId,
          name: AccountStatusEnum[statusChange.statusId]
        }),
        statusChangedDate: statusChange.statusChangedDate
      })
    : account;
}
